import React, { useState, useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Field, FieldArray, reduxForm, change,
} from 'redux-form';
import { connect, useDispatch } from 'react-redux';
import moment from 'moment';
import { withTranslation } from 'react-i18next';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import { Row, Col, Button } from 'reactstrap';

import taskType from '../../Tasks/types/task';

import MatTableHead from '../../../../shared/components/material-table/MatTableHead';
import MatTableToolbar from '../../../../shared/components/material-table/MatTableToolbar';

const changeBillForm = (field, value) => change('tenant_bill_form', field, value);

const getSorting = (order, orderBy) => {
  if (order === 'desc') {
    return (a, b) => {
      if (a[orderBy] < b[orderBy]) {
        return -1;
      }
      if (a[orderBy] > b[orderBy]) {
        return 1;
      }
      return 0;
    };
  }
  return (a, b) => {
    if (a[orderBy] > b[orderBy]) {
      return -1;
    }
    if (a[orderBy] < b[orderBy]) {
      return 1;
    }
    return 0;
  };
};

const SET_ORDER = 'SET_ORDER';

const rowHeads = [
  {
    id: 'title', label: 'Title',
  },
  {
    id: 'startDateTime', label: 'Date',
  },
  {
    id: 'duration', label: 'Duration (hours)',
  },
  {
    id: 'taskType', label: 'Task Type',
  },
];

const BillFormTasks = ({
  taskList, selectedTasks, currency, t,
}) => {
  const dispatch = useDispatch();
  const [order, dispatchOrder] = useReducer((state, action) => {
    switch (action.type) {
      case SET_ORDER:
        return action.data;
      default:
        return state;
    }
  }, { dir: 'desc', by: 'startDateTime' });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const handleRequestSort = (event, by) => {
    const dir = (order.by === by && order.dir === 'desc') ? 'asc' : 'desc';

    dispatchOrder({ type: SET_ORDER, data: { by, dir } });
  };

  const handleSelectAllClick = (event, checked) => {
    let nextTasks = new Set();
    if (checked) {
      nextTasks = new Set(taskList.map(({ _id }) => _id));
    }
    dispatch(changeBillForm('tasks', nextTasks));
  };
  const handleClick = (event, id) => {
    const nextTasks = new Set(selectedTasks);
    if (nextTasks.has(id)) {
      nextTasks.delete(id);
    } else {
      nextTasks.add(id);
    }
    dispatch(changeBillForm('tasks', nextTasks));
  };
  const handleChangePage = (event, nextPage) => {
    setPage(nextPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
  };

  useEffect(() => {
    // select all initially
    handleSelectAllClick(undefined, true);
  }, [taskList]);

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, taskList.length - (page * rowsPerPage));

  return (
    <Row>
      <Col md={12} lg={12}>
        <div className="card__title">
          <h5 className="bold-text">{t('bill_form.select_task')}</h5>
        </div>
        <MatTableToolbar
          numSelected={selectedTasks.size}
          /* onRequestSort={handleRequestSort} */
        />
        <div className="material-table__wrap">
          {
            taskList.length === 0 ? (
              <span>
                {t('bill_form.no_open')}
              </span>
            ) : null
          }
          <Table className="material-table">
            <MatTableHead
              numSelected={selectedTasks.size}
              order={order.dir}
              orderBy={order.by}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={taskList.length}
              heads={rowHeads}
            />
            <TableBody>
              {taskList
                .sort(getSorting(order.dir, order.by))
                .slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage)
                .map((d) => {
                  const isSelected = selectedTasks.has(d._id);
                  return (
                    <TableRow
                      className="material-table__row"
                      role="checkbox"
                      onClick={event => handleClick(event, d._id)}
                      aria-checked={isSelected}
                      tabIndex={-1}
                      key={d._id}
                      selected={isSelected}
                    >
                      <TableCell className="material-table__cell" padding="checkbox">
                        <Checkbox checked={isSelected} className="material-table__checkbox" />
                      </TableCell>
                      <TableCell
                        className="material-table__cell material-table__cell-right"
                        component="th"
                        scope="row"
                        padding="none"
                      >
                        {d.title}
                      </TableCell>
                      <TableCell
                        className="material-table__cell material-table__cell-right"
                      >
                        {moment(d.startDateTime).format('ll')}
                      </TableCell>
                      <TableCell
                        className="material-table__cell material-table__cell-right"
                      >{d.duration} hours
                      </TableCell>
                      <TableCell
                        className="material-table__cell material-table__cell-right"
                      >{d.taskType}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {/*              {emptyRows > 0 && (
                <TableRow style={{ height: 49 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )} */}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          component="div"
          className="material-table__pagination"
          count={taskList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{ 'aria-label': 'Previous Page' }}
          nextIconButtonProps={{ 'aria-label': 'Next Page' }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 40]}
          dir="ltr"
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page' },
            native: true,
          }}
        />
      </Col>
    </Row>
  );
};

BillFormTasks.propTypes = {
  taskList: PropTypes.arrayOf(taskType.isRequired),
  selectedTasks: PropTypes.instanceOf(Set),
  currency: PropTypes.string,
  t: PropTypes.func.isRequired,
};
BillFormTasks.defaultProps = {
  taskList: [],
  selectedTasks: new Set(),
  currency: '',
};


export default reduxForm({
  form: 'tenant_bill_form',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(withTranslation('common')(BillFormTasks));
